/* eslint-disable max-lines */
import moment from 'moment';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, osVersion } from 'react-device-detect';
import beneficiaryIcon from '../../../images/beneficiaryIcon.svg';
import { CustomInput } from '../CustomInput/CustomInput';
import {
  ABC_BENEFICIARY_HEADING_ADD,
  BENEFICIARY_SSN,
  DATE_FORMAT_EN,
  DATE_FORMAT_ES,
  EDUCATIVA_BENEFICIARY_HEADING_ADD,
  EN,
  FIRST_NAME,
  GENDER,
  INVALID_DATE,
  INVALID_FORMAT_ERR,
  IS_STUDENT,
  LAST_NAME,
  MANDATORY_FIELD_ERR,
  MIDDLE_INIT,
  REQUIRED,
  SEC_LAST_NAME,
} from '../../../constants';
import { commonUtility } from '../UtilityFunctions';
import { SSNAndroid } from '../SSN/SSNAndroid';
import {
  commonTernary,
  formatMaskedSSN,
  formatSSN,
  isABCProduct,
  isWebComponentFlow,
} from '../../commonCode';
import { SSN } from '../SSN/SSN';
import { CustomDob } from '../CustomDob/CustomDob';
import { CustomSelect } from '../CustomSelect/CustomSelect';
import BeneficiaryCardComp from './BeneficiaryCardComp';
import { BeneficiaryCompStyles as classes } from './BeneficiaryCompStyles';
import CustomSwitch from '../Switch/CustomSwitch';

const BeneficiaryInfo = () => {
  const { t } = useTranslation();
  const baseContext = isWebComponentFlow();
  const {
    language,
    beneficiaryData,
    setBeneficiaryData,
    beneficiaryDataErrors,
    setBeneficiaryDataErrors,
    flowTypeETB,
    setIsBeneficiaryScreenDirty,
    setIsScreenFiveDirty,
    setIsFundingScreenDirty,
    productName,
    selectedAddress,
    loginHomeAddress,
    addressInformation,
  } = baseContext;
  const isETB = flowTypeETB === 'EtbFlow';
  const isABC = isABCProduct(productName);
  const addressContext = flowTypeETB === 'EtbFlow' ? loginHomeAddress : addressInformation;
  const isAbcEtb = isABC && flowTypeETB === 'EtbFlow';

  useEffect(() => {
    if (beneficiaryData?.addressInfo?.sameBeneficiaryAddress === true || isABC) {
      if (selectedAddress?.AddressId) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          addressInfo: {
            sameBeneficiaryAddress: true,
            [isAbcEtb ? 'beneficiaryMailingAddress1' : 'beneficiaryAddress1']:
              selectedAddress?.LineOne ? selectedAddress.LineOne : '',
            [isAbcEtb ? 'beneficiaryMailingAddress2' : 'beneficiaryAddress2']:
              selectedAddress?.LineTwo ? selectedAddress.LineTwo : '',
            [isAbcEtb ? 'beneficiaryMailingCity' : 'beneficiaryCity']: selectedAddress?.AddrCity
              ? selectedAddress.AddrCity
              : '',
            [isAbcEtb ? 'beneficiaryMailingCountry' : 'beneficiaryCountry']:
              selectedAddress?.AddrCountry,
            [isAbcEtb ? 'beneficiaryMailingState' : 'beneficiaryState']: selectedAddress?.AddrState,
            [isAbcEtb ? 'beneficiaryMailingZipCode' : 'beneficiaryZipCode']:
              selectedAddress?.ZipCode ? selectedAddress.ZipCode : '',
          },
        }));
      } else {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          addressInfo: {
            sameBeneficiaryAddress: true,
            [isAbcEtb ? 'beneficiaryMailingAddress1' : 'beneficiaryAddress1']:
              addressContext?.address1 ? addressContext.address1 : '',
            [isAbcEtb ? 'beneficiaryMailingAddress2' : 'beneficiaryAddress2']:
              addressContext?.address2 ? addressContext.address2 : '',
            [isAbcEtb ? 'beneficiaryMailingCity' : 'beneficiaryCity']: addressContext?.city
              ? addressContext.city
              : '',
            [isAbcEtb ? 'beneficiaryMailingCountry' : 'beneficiaryCountry']:
              addressContext?.country,
            [isAbcEtb ? 'beneficiaryMailingState' : 'beneficiaryState']: addressContext?.state,
            [isAbcEtb ? 'beneficiaryMailingZipCode' : 'beneficiaryZipCode']: addressContext?.zipCode
              ? addressContext.zipCode
              : '',
            ...(isABC &&
              flowTypeETB !== 'EtbFlow' && {
                beneficiaryMailingAddress1: addressContext?.permanentAddress1
                  ? addressContext.permanentAddress1
                  : '',
                beneficiaryMailingAddress2: addressContext?.permanentAddress2
                  ? addressContext.permanentAddress2
                  : '',
                beneficiaryMailingCity: addressContext?.permanentCity
                  ? addressContext.permanentCity
                  : '',
                beneficiaryMailingCountry: addressContext?.permanentCountry,
                beneficiaryMailingState: addressContext?.permanentState,
                beneficiaryMailingZipCode: addressContext?.permanentZipCode
                  ? addressContext.permanentZipCode
                  : '',
              }),
          },
        }));
      }
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        addressInfo: {
          beneficiaryAddress1: '',
          beneficiaryAddress2: '',
          beneficiaryCity: '',
          beneficiaryCountry: '',
          beneficiaryState: '',
          beneficiaryZipCode: '',
          beneficiaryMailingAddress1: '',
          beneficiaryMailingAddress2: '',
          beneficiaryMailingCity: '',
          beneficiaryMailingCountry: '',
          beneficiaryMailingState: '',
          beneficiaryMailingZipCode: '',
        },
      }));
    }
  }, [
    addressInformation,
    loginHomeAddress,
    selectedAddress,
    beneficiaryData?.addressInfo?.sameBeneficiaryAddress,
  ]);

  const handleFirstNameChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.value.match(/^[A-Za-zÑñÁáÉéÍíÓóÚúü]+( [A-Za-zÑñÁáÉéÍíÓóÚúü]+)*$/)) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        firstName: e.target.value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        firstName: '',
      }));
    } else if (e.target.value === '') {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        firstName: '',
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        firstName: '* Mandatory Field',
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        firstName: '* Invalid Format',
      }));
      setBeneficiaryData((prevState) => ({
        ...prevState,
        firstName: e.target.value,
      }));
    }
  };
  const handleLastNameChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.value.match(/^[A-Za-zÑñÁáÉéÍíÓóÚúü-]+( [A-Za-zÑñÁáÉéÍíÓóÚúü-]+)*$/)) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        lastName: e.target.value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        lastName: '',
      }));
    } else if (e.target.value === '') {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        lastName: '',
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        lastName: '* Mandatory Field',
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        lastName: '* Invalid Format',
      }));
      setBeneficiaryData((prevState) => ({
        ...prevState,
        lastName: e.target.value,
      }));
    }
  };
  const handleMiddleNameChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.value.length <= 1) {
      middleAndSecondLastNameChange(e);
    }
  };
  const handleSecondLastNameChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.value.length <= 10) {
      middleAndSecondLastNameChange(e);
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        [e.target.name]: '',
      }));
    }
  };
  const middleAndSecondLastNameChange = (e) => {
    if (e.target.value.match(/^[A-Za-zÑñÁáÉéÍíÓóÚúü]+$/) || e.target.value.length === 0) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        [e.target.name]: '',
      }));
    } else {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        [e.target.name]: '* Invalid Format',
      }));
    }
  };
  const handleKeyDownSSN = (value) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (value.match(/^\d/)) {
      if (value.length <= 11) {
        setBeneficiaryData((prevState) => ({ ...prevState, ssn: value }));
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        if (value.length < 11) {
          setBeneficiaryDataErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
        } else {
          setBeneficiaryDataErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
        }
      }
    } else if ((value ?? '') === '') {
      setBeneficiaryData((prevState) => ({ ...prevState, ssn: '' }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
    }
  };
  const handleKeyDownSSNAndroid = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    const isMasked = e.target.value.includes('X');

    let value = isMasked
      ? formatMaskedSSN(e, beneficiaryData, INVALID_FORMAT_ERR, setBeneficiaryDataErrors)
      : e.target.value;

    if (value.match(/^[0-9-]*$/)) {
      value = isMasked ? value : formatSSN(e, beneficiaryData);
      if (value.length <= 11) {
        setBeneficiaryData((prevState) => ({ ...prevState, ssn: value }));
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          ssn: '',
        }));
        if (value.length < 11 && value.length !== 0) {
          setBeneficiaryDataErrors((prevState) => ({
            ...prevState,
            ssn: '* Social Security Number should have 9 digits',
          }));
        } else if (value.length === 0) {
          setBeneficiaryDataErrors((prevState) => ({
            ...prevState,
            ssn: MANDATORY_FIELD_ERR,
          }));
        } else {
          setBeneficiaryDataErrors((prevState) => ({
            ...prevState,
            ssn: '',
          }));
        }
      }
    } else if ((value ?? '') === '') {
      setBeneficiaryData((prevState) => ({ ...prevState, ssn: '' }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        ssn: MANDATORY_FIELD_ERR,
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        ssn: INVALID_FORMAT_ERR,
      }));
    }
  };
  const handleGenderChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    const { value } = e.target;
    if (value.length > 0) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        gender: value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        gender: '',
      }));
    } else {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        gender: value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        gender: MANDATORY_FIELD_ERR,
      }));
    }
  };
  const handleDateOfBirth = (newValue) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (newValue) {
      const selectedDate = new Date(newValue);
      const currentdate = new Date();
      let age = currentdate.getFullYear() - selectedDate.getFullYear();
      const monthDiff = currentdate.getMonth() - selectedDate.getMonth();
      const dayDiff = currentdate.getDate() - selectedDate.getDate();
      const formattedDate = moment(newValue).format('yyyy-MM-DD');
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age -= 1;
      }
      const notEligible = age >= 21;
      const eighteenthBirthday = new Date(selectedDate);
      eighteenthBirthday.setFullYear(eighteenthBirthday.getFullYear() + 18);
      eighteenthBirthday.setHours(0, 0, 0, 0);
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);
      const greaterThan18 = todayDate > eighteenthBirthday;
      setBeneficiaryData((prevState) => ({
        ...prevState,
        dob: formattedDate,
        ...(isABC && { isIneligible: greaterThan18 }),
      }));
      if (notEligible && !isABC) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          dob: formattedDate,
        }));
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          dob: '* Based on the beneficiary’s age (21 or above), they’re no longer eligible for an IRA Educativa account.',
        }));
      } else if (newValue === 'Invalid Date') {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          dob: formattedDate,
        }));
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          dob: INVALID_DATE,
        }));
      } else if (selectedDate.getTime() > currentdate.getTime()) {
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          dob: INVALID_DATE,
        }));
      } else {
        setBeneficiaryDataErrors((prevState) => ({
          ...prevState,
          dob: '',
        }));
      }
    } else {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        dob: '',
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        dob: MANDATORY_FIELD_ERR,
      }));
    }
  };
  const handleStudentToggle = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.checked === true) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        isStudent: true,
        educationalInstitution: '',
      }));
    } else if (e.target.checked === false) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        isStudent: false,
        educationalInstitution: '',
      }));
    }
    setBeneficiaryDataErrors((prevState) => ({
      ...prevState,
      educationalInstitution: '',
    }));
  };
  const studentToggleOnEnter = (input) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (input === 'isStudent') {
      const currentState = beneficiaryData?.isStudent;
      const toggledState = !currentState;
      if (toggledState === false) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          isStudent: false,
          educationalInstitution: '',
        }));
      }
      if (toggledState === true) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          isStudent: true,
          educationalInstitution: '',
        }));
      }
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        educationalInstitution: '',
      }));
    }
  };
  const handleInstitutionChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (e.target.value.length >= 2 && e.target.value.match(/^[A-Za-z0-9@&_.\-\s,()]*$/)) {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        educationalInstitution: '',
      }));
    } else if (e.target.value.length === 0) {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        educationalInstitution: '* Mandatory Field',
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        educationalInstitution: '* Invalid Educational Institution',
      }));
    }
    setBeneficiaryData((prevState) => ({
      ...prevState,
      educationalInstitution: e.target.value,
    }));
  };

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        ...(!isABC && classes.beneficiaryMainContainer),
        ...(isETB && { paddingTop: '0px', borderTop: 'none' }),
      }}
    >
      <Typography sx={classes.beneficiaryText1}>
        {t('Add Beneficiary', { defaultValue: 'Add Beneficiary' })}
      </Typography>
      <Grid sx={{ marginTop: '20px' }}>
        {!beneficiaryData.captureInfo ? (
          <Grid sx={classes.beneficiarySubContainer1}>
            <Box
              component="img"
              src={beneficiaryIcon}
              alt="Beneficiary Icon"
              width="52px"
              height="52px"
            />
            <Box sx={classes.subHeadingContainer}>
              <Typography sx={classes.beneficiaryText2}>
                {t('A beneficiary is required', { defaultValue: 'A beneficiary is required' })}
              </Typography>
              <Typography sx={classes.beneficiaryText3}>
                {isABC
                  ? t(ABC_BENEFICIARY_HEADING_ADD, {
                      defaultValue: ABC_BENEFICIARY_HEADING_ADD,
                    })
                  : t(EDUCATIVA_BENEFICIARY_HEADING_ADD, {
                      defaultValue: EDUCATIVA_BENEFICIARY_HEADING_ADD,
                    })}
              </Typography>
            </Box>
            <Button
              sx={classes.beneficiaryAddButton}
              onClick={() => {
                setBeneficiaryData((prev) => ({ ...prev, captureInfo: true }));
              }}
            >
              <Typography sx={{ fontSize: '20px', lineHeight: '100%' }}>+</Typography>
              {t('Add Beneficiary', {
                defaultValue: 'Add Beneficiary',
              })}
            </Button>
          </Grid>
        ) : (
          <Grid sx={classes.inputContainer}>
            <Grid container spacing={2} sx={{ display: 'flex' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInput
                  id="beneficiaryFirstName"
                  dataTestId="beneficiaryFirstName_id"
                  required
                  inputLabelAriaLabel={`${t(FIRST_NAME, {
                    defaultValue: FIRST_NAME,
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                  label={t(FIRST_NAME, { defaultValue: FIRST_NAME })}
                  autoFocus={false}
                  handleChange={handleFirstNameChange}
                  placeholder={t('Enter First Name', {
                    defaultValue: 'Enter First Name',
                  })}
                  fullWidth
                  inputProps={{ maxLength: 10, 'data-testid': 'beneficiaryFirstName' }}
                  value={commonUtility(beneficiaryData?.firstName)}
                  error={commonUtility(beneficiaryDataErrors?.firstName)}
                />
                <Box
                  component="span"
                  sx={classes.errorMessage}
                  role="alert"
                  data-testid="beneficiaryFirstNameError"
                >
                  {t(beneficiaryDataErrors?.firstName, {
                    defaultValue: beneficiaryDataErrors?.firstName,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInput
                  id="beneficiaryLastName"
                  dataTestId="beneficiaryLastName_id"
                  required
                  inputLabelAriaLabel={`${t(LAST_NAME, {
                    defaultValue: LAST_NAME,
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                  label={t(LAST_NAME, { defaultValue: LAST_NAME })}
                  handleChange={handleLastNameChange}
                  placeholder={t('Enter Last Name', {
                    defaultValue: 'Enter Last Name',
                  })}
                  fullWidth
                  inputProps={{ maxLength: 15, 'data-testid': 'beneficiaryLastName' }}
                  value={commonUtility(beneficiaryData?.lastName)}
                  error={commonUtility(beneficiaryDataErrors?.lastName)}
                />
                <Box
                  component="span"
                  sx={classes.errorMessage}
                  role="alert"
                  data-testid="beneficiaryLastNameError"
                >
                  {t(beneficiaryDataErrors?.lastName, {
                    defaultValue: beneficiaryDataErrors?.lastName,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInput
                  id="middleName"
                  dataTestId="middleName_id"
                  required={false}
                  inputLabelAriaLabel={`${t(MIDDLE_INIT, {
                    defaultValue: MIDDLE_INIT,
                  })}`}
                  label={t(MIDDLE_INIT, {
                    defaultValue: MIDDLE_INIT,
                  })}
                  autoFocus={false}
                  handleChange={handleMiddleNameChange}
                  placeholder={t('Enter Middle Initial', {
                    defaultValue: 'Enter Middle Initial',
                  })}
                  fullWidth
                  inputProps={{ maxLength: 1, 'data-testid': 'middleName' }}
                  value={commonUtility(beneficiaryData?.middleName)}
                  error={commonUtility(beneficiaryDataErrors?.middleName)}
                />
                <Box
                  component="span"
                  sx={classes.errorMessage}
                  role="alert"
                  data-testid="middleNameError"
                >
                  {t(beneficiaryDataErrors?.middleName, {
                    defaultValue: beneficiaryDataErrors?.middleName,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInput
                  id="secondLastName"
                  dataTestId="secondLastName_id"
                  required={false}
                  inputLabelAriaLabel={`${t(SEC_LAST_NAME, {
                    defaultValue: SEC_LAST_NAME,
                  })}`}
                  label={t(SEC_LAST_NAME, {
                    defaultValue: SEC_LAST_NAME,
                  })}
                  handleChange={handleSecondLastNameChange}
                  placeholder={t('Enter Second Last Name', {
                    defaultValue: 'Enter Second Last Name',
                  })}
                  fullWidth
                  inputProps={{
                    maxLength: 10,
                    'data-testid': 'secondLastName',
                  }}
                  value={commonUtility(beneficiaryData?.secondLastName)}
                  error={commonUtility(beneficiaryDataErrors?.secondLastName)}
                />
                <Box
                  component="span"
                  sx={classes.errorMessage}
                  role="alert"
                  data-testid="secondLastNameError"
                >
                  {t(beneficiaryDataErrors?.secondLastName, {
                    defaultValue: beneficiaryDataErrors?.secondLastName,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {isAndroid && osVersion?.split('.')[0] <= 10 ? (
                  <SSNAndroid
                    handleKeyDown={handleKeyDownSSNAndroid}
                    error={commonUtility(beneficiaryDataErrors?.ssn)}
                    ssn={beneficiaryData.ssn ?? ''}
                    id={BENEFICIARY_SSN}
                  />
                ) : (
                  <SSN
                    handleKeyDown={handleKeyDownSSN}
                    error={commonUtility(beneficiaryDataErrors?.ssn)}
                    ssn={beneficiaryData.ssn ?? ''}
                    id={BENEFICIARY_SSN}
                  />
                )}

                <Box component="span" role="alert" sx={classes.errorMessage}>
                  {t(beneficiaryDataErrors?.ssn, {
                    defaultValue: beneficiaryDataErrors?.ssn,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomDob
                  label={t(`Date of birth`, {
                    defaultValue: `Date of birth`,
                  })}
                  id="beneficiaryDOB"
                  maxDatePicker={new Date()}
                  placeholder={commonTernary(
                    language === EN,
                    t(DATE_FORMAT_EN, { defaultValue: DATE_FORMAT_EN }),
                    t(DATE_FORMAT_ES, { defaultValue: DATE_FORMAT_ES })
                  )}
                  inputFormat={commonTernary(
                    language === EN,
                    t('MM/DD/YYYY', { defaultValue: 'MM/DD/YYYY' }),
                    t('DD/MM/YYYY', { defaultValue: 'DD/MM/YYYY' })
                  )}
                  dataTestIdForLabel="beneficiaryDOBTestId"
                  dataTestId="beneficiaryDate-of-birth"
                  value={beneficiaryData.dob}
                  onChange={handleDateOfBirth}
                  fullWidth
                  required
                  language={language}
                  dob={commonTernary(beneficiaryData?.dob, beneficiaryData.dob, '')}
                  errorBorder={commonUtility(beneficiaryDataErrors?.dob)}
                />
                <Box
                  component="span"
                  role="alert"
                  tabIndex="-1"
                  sx={classes.errorMessage}
                  data-testid="beneficiaryDOBError"
                >
                  {t(beneficiaryDataErrors?.dob, {
                    defaultValue: beneficiaryDataErrors?.dob,
                  })}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomSelect
                  id="beneficiaryGender"
                  dataTestId="beneficiaryGender_id"
                  required
                  inputLabelAriaLabel={`${t(GENDER, {
                    defaultValue: GENDER,
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                  label={t(GENDER, { defaultValue: GENDER })}
                  options={['Female', 'Male']}
                  handleChange={handleGenderChange}
                  fullWidth
                  value={commonUtility(beneficiaryData?.gender)}
                  error={beneficiaryDataErrors?.gender}
                  selectAriaLabel={`${t('Aria Label Gender', {
                    defaultValue: 'Gender Required Select Gender dropdown menu button',
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                />
                <Box
                  component="span"
                  role="alert"
                  sx={classes.errorMessage}
                  data-testid="beneficiaryGenderError"
                >
                  {t(beneficiaryDataErrors?.gender, {
                    defaultValue: beneficiaryDataErrors?.gender,
                  })}
                </Box>
              </Grid>
            </Grid>
            {isABC && (
              <Grid size={{ xs: 12 }}>
                <Grid sx={{ ...classes.toggleContainer, marginTop: '0px' }}>
                  <Typography sx={classes.toggleText}>
                    {t(IS_STUDENT, {
                      defaultValue: IS_STUDENT,
                    })}
                  </Typography>
                  <Box
                    role="application"
                    aria-roledescription={t(IS_STUDENT, {
                      defaultValue: IS_STUDENT,
                    })}
                  >
                    <CustomSwitch
                      id="isStudent"
                      ariaLabelName={`${t(IS_STUDENT, {
                        defaultValue: IS_STUDENT,
                      })}`}
                      defaultData={!!beneficiaryData?.isStudent}
                      ariaLabelledby="toggleone"
                      handleClickCheckbox={handleStudentToggle}
                      toggleCheckboxOnEnter={studentToggleOnEnter}
                      dataTestId="isStudent"
                    />
                  </Box>
                </Grid>
                {beneficiaryData?.isStudent && (
                  <Grid
                    sx={{
                      width: 'calc(50% - 8px)',
                      marginTop: '20px',
                      '@media (max-width:960px)': { width: '100%' },
                    }}
                  >
                    <CustomInput
                      id="educationalInstitution"
                      inputProps={{ maxLength: 40 }}
                      placeholder={t("Enter the institution's name", {
                        defaultValue: "Enter the institution's name",
                      })}
                      fullWidth
                      value={commonUtility(beneficiaryData?.educationalInstitution)}
                      error={commonUtility(beneficiaryDataErrors?.educationalInstitution)}
                      handleChange={handleInstitutionChange}
                      dataTestId="educationalInstitution_testid"
                      required
                      dataTestIdForLabel="educationalInstitution_label"
                      inputLabelAriaLabel={t('Educational Institution', {
                        defaultValue: 'Educational Institution',
                      })}
                      label={t('Educational Institution', {
                        defaultValue: 'Educational Institution',
                      })}
                    />
                    <Box
                      component="span"
                      sx={classes.errorMessage}
                      role="alert"
                      data-testid="educationalInstitutionError"
                    >
                      {t(beneficiaryDataErrors?.educationalInstitution, {
                        defaultValue: beneficiaryDataErrors?.educationalInstitution,
                      })}
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
            <BeneficiaryCardComp />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
export default BeneficiaryInfo;

BeneficiaryInfo.propTypes = {};
